import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CompetitionProvider } from './contexts/CompetitionContext';
import { TopNav } from './components/layout/TopNav';
import { BackToTop } from './components/layout/BackToTop';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';

import Leaderboard from './pages/Leaderboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminCompetitions from './pages/AdminCompetitions';
import AdminCompetitionImportReview from './pages/AdminCompetitionImportReview';
import AdminTeams from './pages/AdminTeams';
import AdminGames from './pages/AdminGames';
import AdminNotifications from './pages/AdminNotifications';
import AdminUsers from './pages/AdminUsers';
import AdminBets from './pages/AdminBets';
import AdminQuestions from './pages/AdminQuestions';
import Predictions from './pages/Predictions';
import PlaceBet from './pages/PlaceBet';
import UserBets from './pages/UserBets';
import UserProfile from './pages/UserProfile';
import UserSettings from './pages/UserSettings';
import UpcomingGames from './pages/UpcomingGames';
import CompletedGames from './pages/CompletedGames';
import {
  COMPETITION_STORAGE_KEY,
  DEFAULT_COMPETITION_SLUG
} from './constants/competition';
import './App.css';

function persistedCompetitionSlug() {
  try {
    return localStorage.getItem(COMPETITION_STORAGE_KEY) || DEFAULT_COMPETITION_SLUG;
  } catch {
    return DEFAULT_COMPETITION_SLUG;
  }
}

function LegacyGameBetRedirect() {
  const { gameId } = useParams();
  return (
    <Navigate to={`/c/${persistedCompetitionSlug()}/games/${gameId}/bet`} replace />
  );
}

function LegacyGameBetsRedirect() {
  const { gameId } = useParams();
  return (
    <Navigate to={`/c/${persistedCompetitionSlug()}/games/${gameId}/bets`} replace />
  );
}

const PrivateRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !user.isAdmin) {
    return <Navigate to={`/c/${persistedCompetitionSlug()}/dashboard`} />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();
  const slug = persistedCompetitionSlug();

  return (
    <>
      <TopNav />
      {user ? <BackToTop /> : null}
      <main className="page-shell pb-10 transition-colors">
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to={`/c/${slug}/dashboard`} />}
          />
          <Route
            path="/register"
            element={!user ? <Register /> : <Navigate to={`/c/${slug}/dashboard`} />}
          />

          <Route
            path="/c/:competitionSlug/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/c/:competitionSlug/upcoming-games"
            element={
              <PrivateRoute>
                <UpcomingGames />
              </PrivateRoute>
            }
          />
          <Route
            path="/c/:competitionSlug/completed-games"
            element={
              <PrivateRoute>
                <CompletedGames />
              </PrivateRoute>
            }
          />
          <Route
            path="/c/:competitionSlug/games/:gameId/bet"
            element={
              <PrivateRoute>
                <PlaceBet />
              </PrivateRoute>
            }
          />
          <Route
            path="/c/:competitionSlug/games/:gameId/bets"
            element={
              <PrivateRoute>
                <UserBets />
              </PrivateRoute>
            }
          />
          <Route
            path="/c/:competitionSlug/leaderboard"
            element={
              <PrivateRoute>
                <Leaderboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/c/:competitionSlug/predictions"
            element={
              <PrivateRoute>
                <Predictions />
              </PrivateRoute>
            }
          />

          <Route
            path="/user/:userId"
            element={
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <UserSettings />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute adminOnly>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/competitions"
            element={
              <PrivateRoute adminOnly>
                <AdminCompetitions />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/competitions/:competitionId/import-review"
            element={
              <PrivateRoute adminOnly>
                <AdminCompetitionImportReview />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/teams"
            element={
              <PrivateRoute adminOnly>
                <AdminTeams />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/games"
            element={
              <PrivateRoute adminOnly>
                <AdminGames />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <PrivateRoute adminOnly>
                <AdminNotifications />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute adminOnly>
                <AdminUsers />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/bets"
            element={
              <PrivateRoute adminOnly>
                <AdminBets />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/questions"
            element={
              <PrivateRoute adminOnly>
                <AdminQuestions />
              </PrivateRoute>
            }
          />

          {/* Legacy paths → competition-scoped URLs */}
          <Route
            path="/dashboard"
            element={<Navigate to={`/c/${slug}/dashboard`} replace />}
          />
          <Route
            path="/upcoming-games"
            element={<Navigate to={`/c/${slug}/upcoming-games`} replace />}
          />
          <Route
            path="/completed-games"
            element={<Navigate to={`/c/${slug}/completed-games`} replace />}
          />
          <Route
            path="/leaderboard"
            element={<Navigate to={`/c/${slug}/leaderboard`} replace />}
          />
          <Route path="/games/:gameId/bet" element={<LegacyGameBetRedirect />} />
          <Route path="/games/:gameId/bets" element={<LegacyGameBetsRedirect />} />

          <Route
            path="/"
            element={
              <Navigate to={user ? `/c/${slug}/dashboard` : '/login'} replace />
            }
          />
        </Routes>
      </main>
      <Toaster
        position="top-right"
        toastOptions={{
          className: '!bg-zinc-900 !text-zinc-50',
          style: { border: '1px solid rgba(255,255,255,0.08)' },
          success: { iconTheme: { primary: '#22c55e', secondary: '#18181b' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#18181b' } }
        }}
      />
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <CompetitionProvider>
              <div className="App">
                <AppContent />
              </div>
            </CompetitionProvider>
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
